import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Avatar, Box, Button, CircularProgress, InputAdornment, TextField, Typography, Chip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SchoolIcon from '@mui/icons-material/School';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import VideocamIcon from '@mui/icons-material/Videocam';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { useDispatch, useSelector } from 'react-redux';
import { navigate } from '../../redux/navigator/action';
import { APP_ROUTES } from '../../types/global/routes.types';
import { userProfileSelector } from '../Dashboard/selectors';
import { canAccessSandboxStudioSelector } from '../../selectors/selectors';
import {
    ensureObsidianDashboardFonts,
    OBSIDIAN,
    OBSIDIAN_BODY_FONT,
    OBSIDIAN_DISPLAY_FONT,
    dashboardShellSx,
} from '../../constants/obsidianDashboardTheme';
import { formatSandboxPrice, resolveSandboxMediaUrl, type SandboxCourse } from '../../utils/sandboxCourseApi';
import {
    fetchMySandboxAssignments,
    fetchMySandboxEnrollments,
    fetchSandboxExplore,
    fetchTeacherOfficeHours,
} from '../../redux/sandbox/actions';
import { plainTextFromHtml } from '../BrandDashboard/contestWizard/richTextUtils';
import { enrollmentResumeMeta, type SandboxEnrollmentRow } from '../../utils/sandboxEnrollmentHelpers';
import { SandboxBookOfficeHoursModal } from '../../components/Sandbox/SandboxBookOfficeHoursModal';
import { SandboxMyOfficeHoursModal } from '../../components/Sandbox/SandboxMyOfficeHoursModal';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';

const CATEGORIES = [
    'Cinematic UGC',
    'Lighting & Sound',
    'Monetization & Pitching',
    'AI Workflows',
    'Scriptwriting & Hooks',
];

const CINEMA = '#0e0e0e';
const RING = 2 * Math.PI * 18;

function ProgressRing({ percent }: { percent: number }) {
    const p = Math.max(0, Math.min(100, percent));
    const offset = RING - (RING * p) / 100;
    return (
        <Box sx={{ position: 'relative', width: 44, height: 44, flexShrink: 0 }}>
            <Box component="svg" viewBox="0 0 44 44" sx={{ width: 44, height: 44, transform: 'rotate(-90deg)' }}>
                <circle cx="22" cy="22" r="18" fill="none" stroke={OBSIDIAN.surfaceContainerHighest} strokeWidth="3" />
                <circle
                    cx="22"
                    cy="22"
                    r="18"
                    fill="none"
                    stroke={OBSIDIAN.secondary}
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray={RING}
                    strokeDashoffset={offset}
                />
            </Box>
            <Typography
                sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: OBSIDIAN_DISPLAY_FONT,
                    fontWeight: 800,
                    fontSize: 10,
                    color: OBSIDIAN.secondary,
                }}>
                {p}%
            </Typography>
        </Box>
    );
}

export function SandboxExplorePage() {
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const user = useSelector(userProfileSelector);
    const canAccessStudio = useSelector(canAccessSandboxStudioSelector);
    const [q, setQ] = useState('');
    const [category, setCategory] = useState<string | undefined>();
    const [minRating, setMinRating] = useState<number | undefined>();
    const [sortByRating, setSortByRating] = useState(false);
    const [items, setItems] = useState<SandboxCourse[]>([]);
    const [featured, setFeatured] = useState<SandboxCourse | null>(null);
    const [enrollments, setEnrollments] = useState<SandboxEnrollmentRow[]>([]);
    const [assignments, setAssignments] = useState<any[]>([]);
    const [officeHours, setOfficeHours] = useState<{
        teacherId: string;
        teacherName: string;
        teacherPhoto?: string | null;
        courseTitle?: string;
    } | null>(null);
    const [officeHoursOpen, setOfficeHoursOpen] = useState(false);
    const [officeHoursTab, setOfficeHoursTab] = useState<'book' | 'sessions'>('book');
    const [myHoursOpen, setMyHoursOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        ensureObsidianDashboardFonts();
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            // Sequential: takeLatest would cancel the first of two parallel explore dispatches.
            const explore: any = await dispatchPromise(
                fetchSandboxExplore({
                    q: q.trim() || undefined,
                    category,
                    min_rating: minRating,
                    sort: sortByRating ? 'rating' : undefined,
                    page: 1,
                }),
            );
            const feat: any = await dispatchPromise(fetchSandboxExplore({ featured: true, page: 1 }));
            const list = explore?.items || [];
            setItems(list);
            const featItems = feat?.items || [];
            setFeatured(featItems[0] || list[0] || null);
        } catch (e: any) {
            showToastMessage(e?.message || 'Could not load Sandbox', 'error');
            setItems([]);
            setFeatured(null);
        } finally {
            setLoading(false);
        }
    }, [q, category, minRating, sortByRating, dispatchPromise]);

    useEffect(() => {
        const t = setTimeout(load, 250);
        return () => clearTimeout(t);
    }, [load]);

    useEffect(() => {
        if (!user?._id) {
            setEnrollments([]);
            setAssignments([]);
            setOfficeHours(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const [enrollRes, assignRes]: any[] = await Promise.all([
                    dispatchPromise(fetchMySandboxEnrollments()),
                    dispatchPromise(fetchMySandboxAssignments()).catch(() => null),
                ]);
                if (cancelled) return;
                const rows = (enrollRes?.enrollments || enrollRes?.data?.enrollments || []) as SandboxEnrollmentRow[];
                setEnrollments(rows.filter(e => e?.course?._id));
                const assigns = assignRes?.assignments || assignRes?.data?.assignments || [];
                setAssignments(Array.isArray(assigns) ? assigns : []);

                const withOh = rows.find(e => e.course?.office_hours_enabled && e.course?.teacher?._id);
                if (withOh?.course?.teacher?._id) {
                    const tid = String(withOh.course.teacher._id);
                    try {
                        const oh: any = await dispatchPromise(fetchTeacherOfficeHours(tid));
                        if (cancelled) return;
                        if (oh?.bookable) {
                            const t = withOh.course.teacher;
                            setOfficeHours({
                                teacherId: tid,
                                teacherName:
                                    [t?.firstName, t?.lastName].filter(Boolean).join(' ') || t?.userName || 'Mentor',
                                teacherPhoto: resolveSandboxMediaUrl(t?.photo),
                                courseTitle: withOh.course.title,
                            });
                            return;
                        }
                    } catch {
                        /* ignore */
                    }
                }
                if (!cancelled) setOfficeHours(null);
            } catch {
                if (!cancelled) {
                    setEnrollments([]);
                    setAssignments([]);
                    setOfficeHours(null);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [user?._id, dispatchPromise]);

    const primaryEnrollment = enrollments[0] || null;
    const primaryResume = useMemo(
        () => (primaryEnrollment ? enrollmentResumeMeta(primaryEnrollment) : null),
        [primaryEnrollment],
    );
    const featuredEnrollment = useMemo(() => {
        if (!featured?._id) return null;
        return enrollments.find(e => String(e.course?._id) === String(featured._id)) || null;
    }, [enrollments, featured]);
    const featuredResume = featuredEnrollment ? enrollmentResumeMeta(featuredEnrollment) : null;

    const openCourse = (id: string) => {
        dispatch(navigate({ routes: APP_ROUTES.SANDBOX_COURSE.replace(':courseId', id) }));
    };

    const openLesson = (courseId: string, lessonId: string) => {
        dispatch(
            navigate({
                routes: APP_ROUTES.SANDBOX_PLAYER.replace(':courseId', courseId).replace(':lessonId', lessonId),
            }),
        );
    };

    const lessonCount = (course: SandboxCourse) =>
        course.lesson_count || (course.modules || []).reduce((n, m) => n + ((m.lessons && m.lessons.length) || 0), 0);

    const moduleCount = (course: SandboxCourse) => course.module_count || (course.modules || []).length || 0;

    const priceLabel = (course: SandboxCourse) => {
        if (course.pricing_mode === 'pro_pass' || course.is_free_for_subscriber) {
            return 'Pro Pass';
        }
        if ((Number(course.price_cents) || 0) <= 0) return 'Included';
        return formatSandboxPrice(course.price_cents, course.currency);
    };

    const teacherName = (course: SandboxCourse) =>
        [course.teacher?.firstName, course.teacher?.lastName].filter(Boolean).join(' ') ||
        course.teacher?.userName ||
        'Instructor';

    const ratingLabel = (course?: SandboxCourse | null) => {
        const count = Number(course?.rating_count) || 0;
        if (!course || count <= 0) return null;
        const avg = Number(course.rating_avg) || 0;
        return `★ ${avg.toFixed(1)} (${count})`;
    };

    const activeAssignment = assignments[0] || null;

    return (
        <Box sx={{ ...dashboardShellSx, bgcolor: CINEMA, pb: 8, px: { xs: 2, md: 3 } }}>
            <Box
                sx={{
                    pt: 2,
                    pb: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 2,
                }}>
                <Box>
                    <Chip
                        size="small"
                        label="Sandbox Live Active"
                        sx={{
                            bgcolor: OBSIDIAN.surfaceContainerHighest,
                            color: OBSIDIAN.primary,
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontWeight: 700,
                            fontSize: 10,
                            mb: 1,
                        }}
                    />
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                            fontWeight: 800,
                            fontSize: { xs: 22, md: 28 },
                            color: OBSIDIAN.onSurface,
                        }}>
                        Masterclasses & Live
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontSize: 12,
                            color: OBSIDIAN.onSurfaceVariant,
                            mt: 0.5,
                            maxWidth: 480,
                        }}>
                        Curated creator curriculum. Buy courses — platform subscribers get member pricing.
                    </Typography>
                </Box>
                {canAccessStudio ? (
                    <Button
                        variant="contained"
                        onClick={() => dispatch(navigate({ routes: APP_ROUTES.SANDBOX_STUDIO }))}
                        sx={{
                            bgcolor: OBSIDIAN.primary,
                            color: OBSIDIAN.onPrimaryFixed,
                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                            fontWeight: 700,
                            textTransform: 'none',
                            borderRadius: 999,
                            whiteSpace: 'nowrap',
                        }}>
                        Teacher Studio
                    </Button>
                ) : null}
            </Box>

            <TextField
                fullWidth
                size="small"
                placeholder="Search masterclasses, mentors…"
                value={q}
                onChange={e => setQ(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ color: OBSIDIAN.onSurfaceVariant, fontSize: 20 }} />
                        </InputAdornment>
                    ),
                }}
                sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                        bgcolor: OBSIDIAN.surfaceContainerHighest,
                        borderRadius: 2,
                        color: OBSIDIAN.onSurface,
                        fontFamily: OBSIDIAN_BODY_FONT,
                        fontSize: 13,
                        '& fieldset': { border: 'none' },
                    },
                }}
            />

            <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1, mb: 1 }}>
                <Chip
                    label="All"
                    onClick={() => setCategory(undefined)}
                    sx={{
                        bgcolor: !category ? OBSIDIAN.primary : OBSIDIAN.surfaceContainerHigh,
                        color: !category ? OBSIDIAN.onPrimaryFixed : OBSIDIAN.onSurfaceVariant,
                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                        fontWeight: 700,
                        fontSize: 12,
                    }}
                />
                {CATEGORIES.map(c => (
                    <Chip
                        key={c}
                        label={c}
                        onClick={() => setCategory(c)}
                        sx={{
                            bgcolor: category === c ? OBSIDIAN.primary : OBSIDIAN.surfaceContainerHigh,
                            color: category === c ? OBSIDIAN.onPrimaryFixed : OBSIDIAN.onSurfaceVariant,
                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                            fontWeight: category === c ? 700 : 500,
                            fontSize: 12,
                            flexShrink: 0,
                        }}
                    />
                ))}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 2, mb: 1 }}>
                {(
                    [
                        { id: 'any', label: 'Any rating', min: undefined as number | undefined, sort: false },
                        { id: 'top', label: 'Top rated', min: undefined, sort: true },
                        { id: '4', label: '4★ & up', min: 4, sort: false },
                        { id: '3', label: '3★ & up', min: 3, sort: false },
                    ] as const
                ).map(opt => {
                    const active =
                        opt.id === 'any'
                            ? !minRating && !sortByRating
                            : opt.id === 'top'
                            ? sortByRating && !minRating
                            : minRating === opt.min && !sortByRating;
                    return (
                        <Chip
                            key={opt.id}
                            label={opt.label}
                            onClick={() => {
                                setMinRating(opt.min);
                                setSortByRating(opt.sort);
                            }}
                            sx={{
                                bgcolor: active ? OBSIDIAN.secondary : OBSIDIAN.surfaceContainerHigh,
                                color: active ? '#004734' : OBSIDIAN.onSurfaceVariant,
                                fontFamily: OBSIDIAN_DISPLAY_FONT,
                                fontWeight: active ? 700 : 500,
                                fontSize: 12,
                                flexShrink: 0,
                            }}
                        />
                    );
                })}
            </Box>
            {/* Continue / featured with progress */}
            {(primaryEnrollment || featured) && (
                <Box
                    sx={{
                        p: 2.5,
                        mb: 3,
                        borderRadius: 3,
                        bgcolor: OBSIDIAN.surfaceContainer,
                        position: 'relative',
                        overflow: 'hidden',
                    }}>
                    <Box
                        sx={{
                            position: 'absolute',
                            right: -48,
                            top: -48,
                            width: 176,
                            height: 176,
                            borderRadius: '50%',
                            bgcolor: 'rgba(255,201,101,0.15)',
                            filter: 'blur(40px)',
                            pointerEvents: 'none',
                        }}
                    />
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LocalFireDepartmentIcon sx={{ fontSize: 14, color: OBSIDIAN.primary }} />
                                <Typography
                                    sx={{
                                        color: OBSIDIAN.primary,
                                        fontSize: 11,
                                        fontWeight: 700,
                                        fontFamily: OBSIDIAN_BODY_FONT,
                                        textTransform: 'uppercase',
                                    }}>
                                    {primaryEnrollment ? 'Continue Learning' : 'Featured Masterclass'}
                                </Typography>
                            </Box>
                            {((primaryEnrollment?.course || featured)?.pricing_mode === 'pro_pass' ||
                                (primaryEnrollment?.course || featured)?.is_free_for_subscriber) && (
                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        color: OBSIDIAN.secondary,
                                        bgcolor: 'rgba(0,253,193,0.15)',
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 1,
                                    }}>
                                    Pro Pass Included
                                </Typography>
                            )}
                        </Box>
                        <Typography
                            onClick={() => openCourse(String((primaryEnrollment?.course || featured)!._id))}
                            sx={{
                                fontFamily: OBSIDIAN_DISPLAY_FONT,
                                fontWeight: 800,
                                fontSize: 18,
                                color: OBSIDIAN.onSurface,
                                cursor: 'pointer',
                            }}>
                            {(primaryEnrollment?.course || featured)?.title}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: OBSIDIAN_BODY_FONT,
                                fontSize: 12,
                                color: OBSIDIAN.onSurfaceVariant,
                                mt: 0.5,
                            }}>
                            {plainTextFromHtml((primaryEnrollment?.course || featured)?.description || '') ||
                                'Cinematic lighting, pacing, and editorial craft for creator brands.'}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, alignItems: 'center' }}>
                            <Typography sx={{ fontSize: 12, color: OBSIDIAN.onSurfaceVariant }}>
                                {teacherName((primaryEnrollment?.course || featured)!)}
                                {moduleCount((primaryEnrollment?.course || featured)!)
                                    ? ` · ${moduleCount((primaryEnrollment?.course || featured)!)} modules`
                                    : ''}
                                {ratingLabel(primaryEnrollment?.course || featured)
                                    ? ` · ${ratingLabel(primaryEnrollment?.course || featured)}`
                                    : ''}
                            </Typography>
                            {!primaryEnrollment && featured && (
                                <Typography
                                    sx={{
                                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                                        fontWeight: 700,
                                        color: OBSIDIAN.primary,
                                    }}>
                                    {priceLabel(featured)}
                                </Typography>
                            )}
                        </Box>

                        {(primaryResume || featuredResume) && (
                            <Box
                                sx={{
                                    mt: 2,
                                    p: 1.5,
                                    borderRadius: 2,
                                    bgcolor: OBSIDIAN.surfaceContainerLow,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1.5,
                                }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                                    <ProgressRing percent={(primaryResume || featuredResume)!.percent} />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            sx={{
                                                fontFamily: OBSIDIAN_DISPLAY_FONT,
                                                fontWeight: 700,
                                                fontSize: 12,
                                                color: OBSIDIAN.onSurface,
                                            }}>
                                            {(primaryResume || featuredResume)!.total
                                                ? `Lesson ${(primaryResume || featuredResume)!.lessonNumber} of ${
                                                      (primaryResume || featuredResume)!.total
                                                  }`
                                                : 'Ready to start'}
                                        </Typography>
                                        <Typography
                                            noWrap
                                            sx={{
                                                fontFamily: OBSIDIAN_BODY_FONT,
                                                fontSize: 10,
                                                color: OBSIDIAN.onSurfaceVariant,
                                            }}>
                                            {(primaryResume || featuredResume)!.lesson?.title || 'Open course to begin'}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Button
                                    startIcon={<PlayCircleIcon sx={{ fontSize: 16 }} />}
                                    onClick={e => {
                                        e.stopPropagation();
                                        const courseId = String((primaryEnrollment || featuredEnrollment)!.course._id);
                                        const lessonId = (primaryResume || featuredResume)!.resumeLessonId;
                                        if (lessonId) openLesson(courseId, lessonId);
                                        else openCourse(courseId);
                                    }}
                                    sx={{
                                        flexShrink: 0,
                                        px: 1.75,
                                        py: 1,
                                        borderRadius: 2,
                                        background: `linear-gradient(90deg, ${OBSIDIAN.primary}, #feb700)`,
                                        color: OBSIDIAN.onPrimaryFixed,
                                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                                        fontWeight: 700,
                                        fontSize: 12,
                                        textTransform: 'none',
                                    }}>
                                    Resume
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Box>
            )}

            {/* Active submission */}
            {activeAssignment && (
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AssignmentTurnedInIcon sx={{ color: OBSIDIAN.primary, fontSize: 18 }} />
                            <Typography
                                sx={{
                                    fontFamily: OBSIDIAN_DISPLAY_FONT,
                                    fontWeight: 700,
                                    fontSize: 13,
                                    color: OBSIDIAN.onSurface,
                                    textTransform: 'uppercase',
                                }}>
                                Active Submission
                            </Typography>
                        </Box>
                        <Typography
                            sx={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: '#ff7351',
                                bgcolor: 'rgba(185,41,2,0.3)',
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                textTransform: 'capitalize',
                            }}>
                            {String(activeAssignment.status || 'submitted').replace(/_/g, ' ')}
                        </Typography>
                    </Box>
                    <Box sx={{ p: 2, borderRadius: 3, bgcolor: OBSIDIAN.surfaceContainer }}>
                        <Typography
                            sx={{
                                fontSize: 10,
                                fontWeight: 800,
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                color: OBSIDIAN.primary,
                                fontFamily: OBSIDIAN_BODY_FONT,
                            }}>
                            {activeAssignment.course?.title || 'Course'}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: OBSIDIAN_DISPLAY_FONT,
                                fontWeight: 700,
                                fontSize: 13,
                                color: OBSIDIAN.onSurface,
                                mt: 0.5,
                            }}>
                            {activeAssignment.class_title || 'Assignment'}
                        </Typography>
                        {activeAssignment.assignment_brief ? (
                            <Typography
                                sx={{
                                    fontFamily: OBSIDIAN_BODY_FONT,
                                    fontSize: 11,
                                    color: OBSIDIAN.onSurfaceVariant,
                                    mt: 1,
                                    lineHeight: 1.5,
                                }}>
                                {activeAssignment.assignment_brief}
                            </Typography>
                        ) : null}
                        {activeAssignment.teacher_review && activeAssignment.status === 'revision_requested' ? (
                            <Typography
                                sx={{
                                    mt: 1,
                                    fontSize: 11,
                                    color: OBSIDIAN.secondary,
                                    fontFamily: OBSIDIAN_BODY_FONT,
                                }}>
                                Mentor: {activeAssignment.teacher_review}
                            </Typography>
                        ) : null}
                        <Button
                            startIcon={<UploadFileIcon sx={{ fontSize: 15 }} />}
                            onClick={() =>
                                openLesson(
                                    String(activeAssignment.course?._id || activeAssignment.course),
                                    String(activeAssignment.lesson),
                                )
                            }
                            sx={{
                                mt: 1.5,
                                width: '100%',
                                py: 1,
                                borderRadius: 2,
                                bgcolor: OBSIDIAN.primary,
                                color: OBSIDIAN.onPrimaryFixed,
                                fontFamily: OBSIDIAN_DISPLAY_FONT,
                                fontWeight: 700,
                                fontSize: 12,
                                textTransform: 'none',
                            }}>
                            Open project &amp; tasks
                        </Button>
                    </Box>
                </Box>
            )}

            {/* Office hours mentorship */}
            {officeHours && (
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <VideocamIcon sx={{ color: OBSIDIAN.secondary, fontSize: 18 }} />
                            <Typography
                                sx={{
                                    fontFamily: OBSIDIAN_DISPLAY_FONT,
                                    fontWeight: 700,
                                    fontSize: 13,
                                    color: OBSIDIAN.onSurface,
                                    textTransform: 'uppercase',
                                }}>
                                Sandbox Mentorship
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    color: OBSIDIAN.secondary,
                                    bgcolor: 'rgba(0,108,80,0.3)',
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                }}>
                                Bookable
                            </Typography>
                            {user?._id ? (
                                <Button
                                    onClick={() => {
                                        setOfficeHoursTab('sessions');
                                        setOfficeHoursOpen(true);
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        color: OBSIDIAN.primary,
                                        fontWeight: 700,
                                        fontSize: 12,
                                        minWidth: 0,
                                    }}>
                                    Manage sessions
                                </Button>
                            ) : null}
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: 3,
                            bgcolor: OBSIDIAN.surfaceContainerLow,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1.5,
                        }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                            <Avatar
                                src={officeHours.teacherPhoto || undefined}
                                sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 2,
                                    bgcolor: OBSIDIAN.surfaceContainerHighest,
                                }}>
                                {officeHours.teacherName.slice(0, 1)}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                                        fontWeight: 700,
                                        fontSize: 12,
                                        color: OBSIDIAN.onSurface,
                                    }}>
                                    Office Hours: Live Q&amp;A
                                </Typography>
                                <Typography
                                    noWrap
                                    sx={{
                                        fontFamily: OBSIDIAN_BODY_FONT,
                                        fontSize: 11,
                                        color: OBSIDIAN.onSurfaceVariant,
                                    }}>
                                    Host: {officeHours.teacherName}
                                    {officeHours.courseTitle ? ` · ${officeHours.courseTitle}` : ''}
                                </Typography>
                            </Box>
                        </Box>
                        <Button
                            startIcon={<VideocamIcon sx={{ fontSize: 14 }} />}
                            onClick={() => {
                                setOfficeHoursTab('book');
                                setOfficeHoursOpen(true);
                            }}
                            sx={{
                                flexShrink: 0,
                                px: 1.5,
                                py: 0.75,
                                borderRadius: 1.5,
                                bgcolor: OBSIDIAN.surfaceContainerHighest,
                                color: OBSIDIAN.secondary,
                                fontFamily: OBSIDIAN_DISPLAY_FONT,
                                fontWeight: 700,
                                fontSize: 12,
                                textTransform: 'none',
                                '&:hover': { bgcolor: OBSIDIAN.secondary, color: '#004734' },
                            }}>
                            Book
                        </Button>
                    </Box>
                </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <TrendingUpIcon sx={{ color: OBSIDIAN.primary, fontSize: 18 }} />
                <Typography
                    sx={{
                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                        fontWeight: 700,
                        fontSize: 13,
                        color: OBSIDIAN.onSurface,
                        textTransform: 'uppercase',
                    }}>
                    Trending Classes
                </Typography>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress sx={{ color: OBSIDIAN.primary }} />
                </Box>
            ) : items.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <SchoolIcon sx={{ fontSize: 40, color: OBSIDIAN.onSurfaceVariant, mb: 1 }} />
                    <Typography sx={{ color: OBSIDIAN.onSurfaceVariant, fontFamily: OBSIDIAN_BODY_FONT }}>
                        No courses yet. Teachers can publish from Studio.
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {items.map(course => {
                        const cover = resolveSandboxMediaUrl(course.cover_image);
                        const lessons = lessonCount(course);
                        const modules = moduleCount(course);
                        const enrolledRow = enrollments.find(e => String(e.course?._id) === String(course._id));
                        const resume = enrolledRow ? enrollmentResumeMeta(enrolledRow) : null;
                        return (
                            <Box
                                key={course._id}
                                onClick={() => openCourse(course._id)}
                                sx={{
                                    display: 'flex',
                                    gap: 1.5,
                                    p: 1.5,
                                    borderRadius: 2,
                                    bgcolor: OBSIDIAN.surfaceContainer,
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: OBSIDIAN.surfaceContainerHigh },
                                }}>
                                <Box
                                    sx={{
                                        width: 96,
                                        height: 96,
                                        flexShrink: 0,
                                        borderRadius: 1.5,
                                        bgcolor: OBSIDIAN.surfaceContainerHighest,
                                        overflow: 'hidden',
                                        position: 'relative',
                                    }}>
                                    {cover ? (
                                        <Box
                                            component="img"
                                            src={cover}
                                            alt=""
                                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    ) : null}
                                    {lessons > 0 && (
                                        <Typography
                                            sx={{
                                                position: 'absolute',
                                                bottom: 6,
                                                left: 6,
                                                px: 0.75,
                                                py: 0.25,
                                                borderRadius: 0.75,
                                                bgcolor: 'rgba(0,0,0,0.75)',
                                                fontSize: 9,
                                                fontWeight: 700,
                                                color: OBSIDIAN.secondary,
                                            }}>
                                            {lessons} class{lessons === 1 ? '' : 'es'}
                                        </Typography>
                                    )}
                                </Box>
                                <Box
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                    }}>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: 10,
                                                color: OBSIDIAN.secondary,
                                                fontWeight: 700,
                                                fontFamily: OBSIDIAN_BODY_FONT,
                                                textTransform: 'uppercase',
                                            }}>
                                            {modules || 1} Module{modules === 1 ? '' : 's'}
                                            {course.categories?.[0] ? ` · ${course.categories[0]}` : ''}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: OBSIDIAN_DISPLAY_FONT,
                                                fontWeight: 700,
                                                fontSize: 13,
                                                color: OBSIDIAN.onSurface,
                                                mt: 0.5,
                                            }}
                                            noWrap>
                                            {course.title}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 11,
                                                color: OBSIDIAN.onSurfaceVariant,
                                                fontFamily: OBSIDIAN_BODY_FONT,
                                            }}
                                            noWrap>
                                            Instructor: {teacherName(course)}
                                        </Typography>
                                        {ratingLabel(course) ? (
                                            <Typography
                                                sx={{
                                                    fontSize: 11,
                                                    color: OBSIDIAN.primary,
                                                    fontFamily: OBSIDIAN_DISPLAY_FONT,
                                                    fontWeight: 700,
                                                    mt: 0.25,
                                                }}
                                                noWrap>
                                                {ratingLabel(course)}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                    {resume ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    height: 4,
                                                    borderRadius: 999,
                                                    bgcolor: OBSIDIAN.surfaceContainerHighest,
                                                    overflow: 'hidden',
                                                }}>
                                                <Box
                                                    sx={{
                                                        width: `${resume.percent}%`,
                                                        height: '100%',
                                                        bgcolor: OBSIDIAN.secondary,
                                                        borderRadius: 999,
                                                    }}
                                                />
                                            </Box>
                                            <Typography
                                                sx={{
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    color: OBSIDIAN.secondary,
                                                    fontFamily: OBSIDIAN_DISPLAY_FONT,
                                                }}>
                                                {resume.percent}%
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 1,
                                            }}>
                                            <Typography
                                                sx={{
                                                    fontFamily: OBSIDIAN_DISPLAY_FONT,
                                                    fontWeight: 700,
                                                    color: OBSIDIAN.primary,
                                                    fontSize: 13,
                                                }}>
                                                {priceLabel(course)}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}

            {!officeHours && user?._id ? (
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        onClick={() => setMyHoursOpen(true)}
                        sx={{ textTransform: 'none', color: OBSIDIAN.primary, fontWeight: 700, fontSize: 12 }}>
                        My office hours
                    </Button>
                </Box>
            ) : null}

            {officeHours ? (
                <SandboxBookOfficeHoursModal
                    open={officeHoursOpen}
                    onClose={() => setOfficeHoursOpen(false)}
                    teacherId={officeHours.teacherId}
                    teacherName={officeHours.teacherName}
                    initialTab={officeHoursTab}
                />
            ) : null}
            {!officeHours ? (
                <SandboxMyOfficeHoursModal open={myHoursOpen} onClose={() => setMyHoursOpen(false)} />
            ) : null}
        </Box>
    );
}
